import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Flex from '@/components/common/Flex';
import { PieChart } from 'echarts/charts';
import * as echarts from 'echarts/core';
import {
  breakpointsMediaQuery,
  getColor,
  useMediaQuery
} from '@/helpers/utils';
import { useRef } from 'react';
import { Card, Col, Row } from 'react-bootstrap';

import BasicECharts from '@/components/common/BasicEChart';
import {
  GridComponent,
  TitleComponent,
  TooltipComponent
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import FalconCardHeader from '@/components/common/FalconCardHeader';
import CardChartDropDown from '@/components/common/CardChartDropDown';
import { useTooltipHide } from '@/hooks/useTooltipHide';
import type { EChartsOption } from 'echarts';
import type { ReactNode } from 'react';
import type ReactEChartsCore from 'echarts-for-react/lib/core';

echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  PieChart,
  CanvasRenderer
]);

type PieChartData = {
  id?: string | number;
  value: number | string | null | undefined;
  name: string;
  color: string;
};

type PieChartFormatterParams = {
  data?: {
    name?: string;
  };
  percent?: number;
};

// تنظیمات نمودار دایره ای در نمای کلی
const getOptions = (
  data: PieChartData[],
  radius: string | number | Array<string | number>,
  percent: string,
  isMdUp: boolean
): EChartsOption => ({
  textStyle: {
    fontFamily: 'dana'
  },
  color: data.map(item => getColor(item.color)),
  tooltip: {
    triggerOn: isMdUp ? 'mousemove' : 'click',
    padding: [7, 10],
    transitionDuration: 0,
    backgroundColor: getColor('gray-100'),
    borderColor: getColor('gray-300'),
    textStyle: { color: getColor('gray-1100'), fontFamily: 'dana !important' },
    borderWidth: 1,
    confine: true,
    // نحوه نمایش تولتیپ نمودار دایره ای
    formatter: (params: PieChartFormatterParams) =>
      `<div><strong>${params.data?.name}:</strong> ${params.percent}%</div>`.toPersianDigit()
  },
  series: [
    {
      name: percent + '%',
      type: 'pie',
      radius,
      avoidLabelOverlap: false,
      emphasis: {
        scale: false
      },
      itemStyle: {
        borderWidth: 2,
        borderColor: getColor('gray-100')
      },
      label: {
        show: true,
        position: 'center',
        formatter: '{a}',
        fontSize: 23,
        color: getColor('gray-1100'),
        fontWeight: 600
      },
      data
    }
  ]
} as unknown as EChartsOption);
// متون استفاده شده در کنار نمودار دایره ای
const ItemList = ({ data }: { data: PieChartData[] }) => {
  return (
    <>
      {data.map((item, index) => (
        <Flex
          key={index}
          alignItems="center"
          justifyContent="start"
          className={`fw-semi-bold fs--2 fs-sm--1 gap-1 ${index == 0 && 'mt-3'}`}
        >
          <FontAwesomeIcon icon="circle" color={getColor(item.color)} />
          {item.name}:<div className="fw-black">{item.value}</div>
        </Flex>
      ))}
    </>
  );
};
// نمودارهای دایره ای در نمای کلی
type CardPieChartProps = {
  data: PieChartData[];
  radius: string | number | Array<string | number>;
  title: ReactNode;
  type?: string;
  tooltip?: ReactNode;
};

const CardPieChart = ({
  data,
  radius,
  title,
  type
}: CardPieChartProps) => {
  const cardRef = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<ReactEChartsCore | null>(null);
  const isMdUp = useMediaQuery(breakpointsMediaQuery.md);
  useTooltipHide(chartRef);
  const colorMaker = (index: number) => {
    if (index % 3 === 0) {
      return 'primary';
    } else if (index % 2 === 0) {
      return 'success';
    } else {
      return 'warning';
    }
  };
  let chartData = data;
  if (type === 'crawlDepth') {
    const response = chartData.map((depth, index) => ({
      id: depth.name,
      value: depth.value,
      name: `سطح ${depth.name}`,
      color: colorMaker(index)
    }));

    chartData = response;
  }

  return (
    <Card ref={cardRef} className="h-md-100">
      <FalconCardHeader
        title={
          <div>
            {title}
            {/* <HelpTooltip html={tooltip} /> */}
          </div>
        }
        titleTag="h6"
        className="py-2"
        light
        endEl={<CardChartDropDown cardRef={cardRef} chartRef={chartRef} />}
      />
      <Card.Body className="pt-2">
        <Row className="justify-content-between g-0">
          <Col xs={6} sm={6} xxl className="pe-2">
            <ItemList data={chartData} />
          </Col>
          <Col
            xs={6}
            sm={6}
            className="ps-0 d-flex align-items-center h-100 justify-content-end"
          >
            <BasicECharts
              ref={chartRef}
              echarts={echarts}
              options={getOptions(
                chartData,
                radius,
                // قرار دادن درصد بیشترین مقدار به عنوان مورد اول در وسط دایره
                Math.floor(
                  (Math.max(
                    ...chartData.map(element => Number(element.value))
                  ) /
                    chartData.reduce(
                      (sum, element) => sum + Number(element.value),
                      0
                    )) *
                    100
                )?.toString(),
                isMdUp
              )}
              style={{ width: '6.625rem', height: '6.625rem' }}
            />
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};

export default CardPieChart;
